import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography, Drawer, Box, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import FilePreview from 'components/Common/FilePreview/FilePreview';
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useWaterfallAnalyses from 'hooks/useWaterfallAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { waterfallAnalysesSelectors } from 'features/waterfallAnalyses';

const WaterfallAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { waterfall_analysis_id } = useParams();
  const { getWaterfallAnalysis, clearLocalActiveWaterfallAnalysis } = useWaterfallAnalyses();
  const { getWaterfallAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const appointmentAnalysisFetching = useCustomSelector(waterfallAnalysesSelectors.waterfallAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(null);
  const [firstResult, setFirstResult] = useState(null);
  const [secondResult, setSecondResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchWaterfallAnalysis = useCallback((id) => {
    setOpen(true);
    getWaterfallAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { waterfall_analysis } } = payload;
        handleSetData(waterfall_analysis);
      }
    })
  }, [getWaterfallAnalysis]);

  const handleSetData = (waterfall_analysis) => {
    setInput(waterfall_analysis?.input);
    setFirstResult(waterfall_analysis?.first_result);
    setSecondResult(waterfall_analysis?.second_result);
  };

  const handleExiting = () => {
    setInput(null);
    setFirstResult(null);
    setSecondResult(null);
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl87e`);
    clearLocalActiveWaterfallAnalysis();
    clearLocalAnalysesEvents();
  };
  
  const handleClose = () => setOpen(false);

  const fetchWaterfallAnalysesEvents = useCallback((offset, waterfall_analysis_id) => {
    getWaterfallAnalysesEvents({ offset, waterfall_analysis_id })
  }, [ getWaterfallAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchWaterfallAnalysesEvents(0, waterfall_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!waterfall_analysis_id) fetchWaterfallAnalysis(waterfall_analysis_id);
  }, [waterfall_analysis_id, fetchWaterfallAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        {appointmentAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {waterfall_analysis_id}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center'  }}>
            {input?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Input:</Typography>
              <FilePreview
                key={input?.id}
                type={input?.content_type}
                name={input?.filename}
                url={input?.url}
              />
            </Box>}
            {firstResult?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>First Result:</Typography>
              <FilePreview
                key={firstResult?.id}
                type={firstResult?.content_type}
                name={firstResult?.filename}
                url={firstResult?.url}
              />
            </Box>}
            {secondResult?.id && <Box sx={{ mb: '30px' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 400, mb: '10px' }}>Second Result:</Typography>
              <FilePreview
                key={secondResult?.id}
                type={secondResult?.content_type}
                name={secondResult?.filename}
                url={secondResult?.url}
              />
            </Box>}
          </Box>
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={waterfall_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchWaterfallAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <WaterfallAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);